import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { setIsLoading } from "../common/commonSlice";

/**
 * V-Resume — the semi-automatic CV builder.
 *
 * ── Why almost nothing here shows the global loader ──────────────────────────────────
 *
 * V-Resume saves as you go. `setIsLoading(true)` puts a full-screen spinner over the app,
 * and a spinner that flashes every time an autosave fires would make the editor unusable —
 * it would also steal focus from the field being typed in. So only the two genuinely
 * blocking, user-initiated operations take it: opening a CV and exporting the PDF.
 *
 * Autosaves report through `saveState` instead ('idle' | 'saving' | 'saved' | 'error'),
 * which the editor renders as a small inline indicator next to the section being edited.
 */

/**
 * Language levels, in the order a CV lists them (strongest first).
 *
 * Must stay in sync with `LANGUAGE_LEVELS` in the backend's `helpers/vresume.js`, which
 * drops any level not in its own allowlist — a value added here and not there would be
 * silently discarded on save.
 */
export const VRESUME_LANGUAGE_LEVELS = ['native', 'C2', 'C1', 'B2', 'B1', 'A2', 'A1']

/**
 * The status pill colours, as VPlatform brand tokens rather than MUI palette names.
 *
 * Follows `getTagClass()` in app/utils/helpers.js so a pill here reads the same as one
 * anywhere else in the app: grey for not started, orange for in progress, green for done.
 * `none` is a collab who has never opened the builder, which the backend reports distinctly
 * from `draft` precisely so the two can look different.
 */
export const VRESUME_STATUS_COLORS = {
    none: { backgroundColor: 'var(--lightGrey)', color: '#3a3a3a' },
    draft: { backgroundColor: 'var(--orange)', color: '#fff' },
    complete: { backgroundColor: 'var(--green)', color: '#fff' }
}

/** The keys the backend returns in `missingSections`, in the order the editor shows them. */
export const VRESUME_SECTION_KEYS = [
    'headline', 'summary', 'experiences', 'bullets', 'education', 'skills', 'languages'
]

const initialState = {
    /** The management listing: one row per collab in the caller's reach. */
    list: [],
    /**
     * Mirrors the backend's own answer to "how far does this user reach".
     *
     * Taken from the response rather than recomputed from the permissions array on the
     * client: the server already decided it when it filtered the rows, and having the UI
     * derive the same conclusion a second time is how the two drift apart. It only ever
     * decides which controls are worth drawing — the server authorises every call again.
     */
    reach: { viewAll: false, editAll: false, hasTeamReach: false },

    /** The CV currently open in the editor. */
    resume: null,
    experiences: [],
    /** The live `collabs` identity the CV header is built from — never stored on the CV. */
    collab: null,
    /** `{ canEdit, isSelf }` for the open CV, straight from the server. */
    access: { canEdit: false, isSelf: false },
    missingSections: [],
    /** How many Vital missions the last read/sync newly imported. Drives the "N missions
     *  imported" toast, then is cleared. */
    syncedMissions: 0,

    postes: [],

    /** Which collab's CV the editor is on. null = the listing is showing. */
    editingCollabId: null,

    /** 'idle' | 'saving' | 'saved' | 'error' — the inline autosave indicator. */
    saveState: 'idle',
    lastSavedAt: null,
    isExporting: false
}

export const VResumeSlice = createSlice({
    name: "vresume",
    initialState,
    reducers: {
        openEditor: (state, action) => {
            state.editingCollabId = action.payload
            state.saveState = 'idle'
        },
        closeEditor: (state) => {
            state.editingCollabId = null
            state.resume = null
            state.experiences = []
            state.collab = null
            state.access = { canEdit: false, isSelf: false }
            state.missingSections = []
            state.saveState = 'idle'
        },
        /**
         * Applies a field change to the local resume immediately, before the debounced save
         * fires.
         *
         * Without this the inputs would be controlled by a value that only updates after a
         * round trip, which reads as dropped keystrokes. The server's response overwrites
         * this optimistically-applied state when it lands.
         */
        patchResumeLocal: (state, action) => {
            if (!state.resume) return
            state.resume = { ...state.resume, ...action.payload }
        },
        /** Same, for one experience block. */
        patchExperienceLocal: (state, action) => {
            const { id, changes } = action.payload
            state.experiences = state.experiences.map(
                experience => experience.id === id ? { ...experience, ...changes } : experience)
        },
        /**
         * Applies a reorder locally so the dragged row stays where it was dropped.
         *
         * The server is told separately and does not answer with the new list; letting the
         * row snap back to its old position until a refetch is the thing this avoids.
         */
        reorderExperiencesLocal: (state, action) => {
            state.experiences = action.payload
        },
        setSaveState: (state, action) => {
            state.saveState = action.payload
        },
        clearSyncedMissions: (state) => {
            state.syncedMissions = 0
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getVResume.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (!data || data.status !== 1) return
            state.resume = data.resume || null
            state.experiences = data.experiences || []
            state.collab = data.collab || null
            state.access = data.access || { canEdit: false, isSelf: false }
            state.missingSections = data.missingSections || []
            state.syncedMissions = data.syncedMissions || 0
            state.lastSavedAt = (data.resume && data.resume.last_saved_at) || null
            state.saveState = 'idle'
        })

        builder.addCase(listVResumes.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (!data || data.status !== 1) return
            state.list = data.list || []
            state.reach = data.reach || initialState.reach
        })

        builder.addCase(getVResumePostes.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (data && data.list) state.postes = data.list
        })

        builder.addCase(saveVResume.pending, (state) => {
            state.saveState = 'saving'
        })
        builder.addCase(saveVResume.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (!data || data.status !== 1) {
                state.saveState = 'error'
                return
            }
            // The server's completion, status and missing-section list are authoritative —
            // all three are recomputed from the persisted row, so they cannot disagree with
            // what was saved. The resume itself is NOT overwritten here: a response landing
            // mid-typing would replace the field under the cursor with the value from one
            // keystroke ago.
            //
            // `status` is included because it is DERIVED from completion server-side. Without
            // carrying it back, a CV that just crossed 100% would keep showing "In progress"
            // until a full reload — which is the bug that made it derived in the first place.
            if (state.resume) {
                state.resume = {
                    ...state.resume,
                    completion: data.completion,
                    status: data.resumeStatus || state.resume.status,
                    last_saved_at: data.last_saved_at
                }
            }
            state.missingSections = data.missingSections || []
            state.lastSavedAt = data.last_saved_at || null
            state.saveState = 'saved'
        })
        builder.addCase(saveVResume.rejected, (state) => {
            state.saveState = 'error'
        })

        builder.addCase(syncVResumeMissions.fulfilled, (state, action) => {
            const data = action.payload && action.payload.responseData
            if (!data || data.status !== 1) return
            state.experiences = data.experiences || state.experiences
            state.syncedMissions = data.syncedMissions || 0
        })

        builder.addCase(exportVResumePdf.pending, (state) => {
            state.isExporting = true
        })
        builder.addCase(exportVResumePdf.fulfilled, (state) => {
            state.isExporting = false
        })
        builder.addCase(exportVResumePdf.rejected, (state) => {
            state.isExporting = false
        })
    }
})

/**
 * Opens a CV. `id_collab` omitted means the caller's own — the backend defaults to the
 * session, so the collab-facing screen never has to know its own id.
 *
 * Takes the global loader: this is a blocking, user-initiated navigation, and the editor
 * has nothing to render until it lands.
 */
export const getVResume = createAsyncThunk(
    "vresume/get",
    async (body) => {
        try {
            store.dispatch(setIsLoading(true))
            const response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.get, body || {})
            store.dispatch(setIsLoading(false))
            return response
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return error
        }
    }
)

export const listVResumes = createAsyncThunk(
    "vresume/list",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.list, body || {})
        } catch (error) {
            return error
        }
    }
)

export const getVResumePostes = createAsyncThunk(
    "vresume/getPostes",
    async () => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.getPostes, {})
        } catch (error) {
            return error
        }
    }
)

/**
 * The autosave. PATCH semantics — send only the fields that changed.
 *
 * No global loader, by design: see the module comment. The `saveState` transitions in
 * `extraReducers` are what the user sees.
 */
export const saveVResume = createAsyncThunk(
    "vresume/save",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.save, body)
        } catch (error) {
            return error
        }
    }
)

/** Adds a block when `id` is absent, patches it when present. */
export const saveVResumeExperience = createAsyncThunk(
    "vresume/saveExperience",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.saveExperience, body)
        } catch (error) {
            return error
        }
    }
)

/**
 * Removes a block.
 *
 * A Vital-sourced block is excluded rather than deleted (the backend answers
 * `{ excluded: true }`), because the mission still exists in `team_members` and the sync
 * would re-add a deleted one. The view surfaces the difference in its confirm dialog.
 */
export const deleteVResumeExperience = createAsyncThunk(
    "vresume/deleteExperience",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.deleteExperience, body)
        } catch (error) {
            return error
        }
    }
)

export const reorderVResumeExperiences = createAsyncThunk(
    "vresume/reorderExperiences",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.reorderExperiences, body)
        } catch (error) {
            return error
        }
    }
)

export const syncVResumeMissions = createAsyncThunk(
    "vresume/sync",
    async (body) => {
        try {
            return await NetworkManager.getInstance().postRequest(
                config.apiPaths.vresume.sync, body || {})
        } catch (error) {
            return error
        }
    }
)

/**
 * Downloads the rendered CV.
 *
 * Goes through `fetchFile`, not `postRequest`: the endpoint answers a PDF stream, and
 * `postRequest` would try to parse those bytes as JSON. `fetchFile` sets
 * `responseType: 'blob'` and triggers the browser download.
 */
export const exportVResumePdf = createAsyncThunk(
    "vresume/exportPdf",
    async ({ idCollab, filename }) => {
        try {
            const body = idCollab ? { id_collab: idCollab } : {}
            await NetworkManager.getInstance().fetchFile(
                config.apiPaths.vresume.exportPdf, body, filename)
            return { responseData: { status: 1 } }
        } catch (error) {
            return { responseError: error }
        }
    }
)

export const {
    openEditor, closeEditor, patchResumeLocal, patchExperienceLocal,
    reorderExperiencesLocal, setSaveState, clearSyncedMissions
} = VResumeSlice.actions

export default VResumeSlice.reducer
